import { useTranslation } from 'next-i18next';

import { FIELD_LABEL } from '@/constants/field-classes';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';

// Same material and the same reasoning as sign-up-modal-skeleton.tsx — see that file for why the bones
// carry no radius of their own.
const BONE = 'animate-pulse bg-white/10';

// Chrome copied verbatim from Dialog.Panel in sign-in-modal.tsx, minus `transition-all` (nothing
// transitions here). Being identical is what makes the skeleton-to-panel swap invisible.
const PANEL = 'relative flex w-full max-w-[480px] flex-col gap-5 overflow-hidden rounded-2xl border border-white/10 bg-[#0f1215] bg-[linear-gradient(135deg,rgba(156,255,46,0.07),rgba(255,255,255,0.02)_48%,rgba(46,230,255,0.05))] p-5 text-start align-middle shadow-[0_40px_120px_-40px_rgba(0,0,0,0.95)]';

// 38px is FIELD_INPUT once it lands: SIZE_SM's py-2 plus a 20px text line box plus 2px of border.
const FIELD_BAR = `${BONE} h-[38px] w-full rounded-[10px]`;

// The two fields on the sign-in path. The forgot-password path has one, but the modal always opens on
// this one.
const FIELD_KEYS = ['login.placeholder.email', 'login.placeholder.password'];

/**
 * Placeholder for the lazily loaded SignInModal, with the real panel's geometry.
 *
 * It exists because the header's Login button is server-rendered while the panel it opens is not: the
 * form machinery behind it (@headlessui, yup, react-hook-form, the captcha providers) was ~40 KB gzip of
 * the first-load bundle of every page with a header, for a dialog that is closed. Without a placeholder
 * the click would land on nothing while that chunk is in flight.
 *
 * Shows the real title and the real field labels — the `common` namespace is already loaded by the parent,
 * so a grey bar over text we already have would be a gratuitous lie and would cost line-box accuracy.
 * Only the inputs and the buttons, whose content depends on state we do not have yet, are bones.
 *
 * No SSO block on purpose: it mirrors sign-up-modal-skeleton, where the same choice is explained —
 * useFeatureToggle starts disabled, so SsoProviders returns null on the real panel's first paint too.
 */
export function SignInModalSkeleton() {
  const { t } = useTranslation('common');

  return (
    <div className='fixed inset-0 z-[1001] overflow-y-auto' aria-hidden='true' data-np-modal-surface=''>
      <div className='fixed inset-0 bg-black/60 backdrop-blur-sm' />
      <div className={`relative flex min-h-full flex-col items-center justify-center gap-5 p-4 text-center ${bai_jamjuree.className}`}>
        <div className={PANEL}>
          <div className='flex items-start justify-between gap-3'>
            <h3 className='text-xl font-black text-[var(--np-fg)]'>{t('login.already_registered')}</h3>
            <span className={`${BONE} size-9 shrink-0 rounded-lg`} />
          </div>

          <div className='flex flex-col gap-5'>
            {FIELD_KEYS.map((key) => (
              <div key={key} className='grid gap-2 text-start'>
                <span className={FIELD_LABEL}>{t(key)}</span>
                <span className={FIELD_BAR} />
              </div>
            ))}

            {/* The captcha box, then the submit row. */}
            <span className={`${BONE} h-[78px] w-full rounded-[10px]`} />
            <span className={`${BONE} h-[38px] w-full rounded-[10px]`} />

            <div className='flex flex-col gap-5 border-t border-white/10 pt-5'>
              <div className='flex flex-col gap-1'>
                <h3 className='text-lg font-black text-[var(--np-fg)]'>{t('login.not_registered')}</h3>
                <p className='text-sm font-medium leading-normal text-[#f4f6f7b8]'>{t('login.not_registered_description')}</p>
              </div>
              <span className={`${BONE} h-[38px] w-full rounded-[10px]`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
